import { match } from 'ts-pattern'
import { ProductIcon } from './ProductIcon'
import { formatters } from '~/utils'
import { QuotaType } from '@bpinternal/const'
import { AddonDetails, AddonQuotaType } from '../types/index'

export const AddonChangeDialog = ({
  addons,
  addonDeltas,
}: {
  addons: AddonDetails[]
  addonDeltas: Record<AddonQuotaType, number>
}) => {
  const currentDate = new Date()
  const nextBillingDate = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1)

  const addonsAdded = addons
    .filter((addon) => addonDeltas[addon.quotaName] > 0)
    .map((addon) => {
      const addonAddedText = match(addon.displayName)
        .with('Bot', () => {
          if (addonDeltas[addon.quotaName] === 1) return `Added ${addonDeltas[addon.quotaName]} bot`
          return `Added ${addonDeltas[addon.quotaName]} bots`
        })
        .with('Collaborators', () => {
          if (addonDeltas[addon.quotaName] === 1) return `Added ${addonDeltas[addon.quotaName]} seat`
          return `Added ${addonDeltas[addon.quotaName]} seats`
        })
        .with('Table Rows', () => {
          return `Added ${formatters.numberDefault(addonDeltas[addon.quotaName] * 100000)} rows`
        })
        .with('Messages & Events', () => {
          return `Added ${formatters.numberDefault(addonDeltas[addon.quotaName] * 5000)} per month`
        })
        .with('Always Alive', () => {
          return `Added ${addonDeltas[addon.quotaName]} Always Alive`
        })
        .with('Vector DB Storage', () => {
          return `Added ${addonDeltas[addon.quotaName]}GB`
        })
        .with('File Storage', () => {
          return `Added ${addonDeltas[addon.quotaName] * 10}GB`
        })
        .otherwise(() => '')

      return {
        text: addonAddedText,
        ...addon,
      }
    })

  const addonsRemoved = addons
    .filter((addon) => addonDeltas[addon.quotaName] < 0)
    .map((addon) => {
      const absoluteDelta = addonDeltas[addon.quotaName] * -1

      const addonRemovedText = match(addon.displayName)
        .with('Bot', () => {
          if (absoluteDelta === 1) return `Removed ${absoluteDelta} bot`
          return `Removed ${absoluteDelta} bots`
        })
        .with('Collaborators', () => {
          if (absoluteDelta === 1) return `Removed ${absoluteDelta} seat`
          return `Removed ${absoluteDelta} seats`
        })
        .with('Table Rows', () => {
          return `Removed ${formatters.numberDefault(absoluteDelta * 100000)} rows`
        })
        .with('Messages & Events', () => {
          return `Removed ${formatters.numberDefault(absoluteDelta * 5000)} per month`
        })
        .with('Always Alive', () => {
          return `Removed ${absoluteDelta} Always Alive`
        })
        .with('Vector DB Storage', () => {
          return `Removed ${absoluteDelta}GB`
        })
        .with('File Storage', () => {
          return `Removed ${absoluteDelta * 10}GB`
        })
        .otherwise(() => '')

      return {
        text: addonRemovedText,
        ...addon,
      }
    })

  return (
    <div className="flex flex-col gap-6">
      {addonsAdded.length > 0 && (
        <div className="flex flex-col gap-2 rounded-xl bg-gray-2 p-4 text-foreground">
          <p className="py-4 text-sm font-medium text-muted-foreground">Added to Subscription</p>
          {addonsAdded.map((addon) => {
            return (
              <div key={addon.displayName} className="flex w-full items-center justify-between gap-2 text-sm">
                <div className="flex items-center gap-2">
                  <ProductIcon
                    productQuotaName={addon.quotaName as QuotaType}
                    className="rounded-lg bg-blue-9 p-1 text-white"
                  />
                  <div className="text-sm font-medium">{addon.displayName}</div>
                </div>
                <div className="text-sm">{addon.text}</div>
              </div>
            )
          })}
          <p className="py-2 text-sm text-muted-foreground">
            You will immediately be charged a prorated amount for the remainder of the current month.
          </p>
        </div>
      )}

      {addonsRemoved.length > 0 && (
        <div className="flex flex-col gap-2 rounded-xl bg-gray-2 p-4 text-foreground">
          <p className="py-4 text-sm font-medium text-muted-foreground">Removed from Subscription</p>
          {addonsRemoved.map((addon) => {
            return (
              <div key={addon.displayName} className="flex w-full items-center justify-between gap-2 text-sm">
                <div className="flex items-center gap-2">
                  <ProductIcon
                    productQuotaName={addon.quotaName as QuotaType}
                    className="rounded-lg bg-blue-9 p-1 text-white"
                  />
                  <div className="text-sm font-medium">{addon.displayName}</div>
                </div>
                <div className="text-sm text-danger">{addon.text}</div>
              </div>
            )
          })}
          <p className="py-2 text-sm text-muted-foreground">
            You will still have access until{' '}
            <span className="font-semibold ">
              {nextBillingDate.toLocaleDateString('en-US', {
                month: 'short',
                day: 'numeric',
                year: 'numeric',
              })}
            </span>
          </p>
        </div>
      )}
    </div>
  )
}
